package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.rememberPageContext
import dev.lingvo.shared.MemberDto
import dev.lingvo.shared.Role
import dev.lingvo.shared.WorkspaceDeletionScopeDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Async
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.loadAsync
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConfirmDialog
import dev.lingvo.site.ds.LDangerIcon
import dev.lingvo.site.ds.LDivider
import dev.lingvo.site.ds.LEmptyState
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LSettingsIcon
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.ToastHost
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.rememberToasts
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.nav.label
import dev.lingvo.site.pages.app.projects.slug.settings.ProjectSettingsSurface
import dev.lingvo.site.pages.app.projects.slug.settings.SettingsFormMeasure
import dev.lingvo.site.pages.app.projects.slug.settings.settingsOperationResult
import dev.lingvo.site.state.WorkspaceSelection
import kotlinx.coroutines.launch

/**
 * What this workspace is called, and how to leave it.
 *
 * Read-only for translators and viewers: they may see which workspace they are in — that is
 * ordinary orientation — and change nothing about it.
 */
@Page("/app/settings/general")
@Layout(".components.layouts.AppLayout")
@Composable
fun WorkspaceGeneralPage() {
    val ctx = rememberPageContext()
    val toasts = rememberToasts()
    val scope = rememberCoroutineScope()
    val workspace = WorkspaceSelection.current()
    val canManage = workspace?.role == Role.OWNER || workspace?.role == Role.ADMIN

    var name by remember(workspace?.slug) { mutableStateOf(workspace?.name.orEmpty()) }
    var slug by remember(workspace?.slug) { mutableStateOf(workspace?.slug.orEmpty()) }
    var saving by remember { mutableStateOf(false) }
    var confirmingLeave by remember { mutableStateOf(false) }
    var leaving by remember { mutableStateOf(false) }
    var leaveError by remember { mutableStateOf<String?>(null) }
    var transferTo by remember(workspace?.slug) { mutableStateOf("") }
    var transferPassword by remember { mutableStateOf("") }
    var pendingTransfer by remember { mutableStateOf<MemberDto?>(null) }
    var transferring by remember { mutableStateOf(false) }
    var transferError by remember { mutableStateOf<String?>(null) }
    var deletePassword by remember { mutableStateOf("") }
    var confirmingDelete by remember { mutableStateOf(false) }
    var deleting by remember { mutableStateOf(false) }
    var deleteError by remember { mutableStateOf<String?>(null) }
    var dangerKey by remember { mutableStateOf(0) }

    val isOwner = workspace?.role == Role.OWNER
    val workspaceSlug = workspace?.slug.orEmpty()
    // Both loaded only for an owner: a member who cannot delete has no reason to be told how much
    // there is to lose, and the deletion-scope endpoint refuses them anyway.
    //
    // `dangerKey` keys both, so the Retry offered on a failed load actually re-runs it, and so a
    // finished handover re-reads the member list it just changed.
    val members = loadAsync(workspace?.slug, isOwner, dangerKey) {
        workspace?.slug?.takeIf { isOwner }?.let { ApiClient.workspaceMembers(it) }
    }
    val deletionScope = loadAsync(workspace?.slug, isOwner, dangerKey) {
        workspace?.slug?.takeIf { isOwner }?.let { ApiClient.workspaceDeletionScope(it) }
    }
    // One sentence, read in two places — on the card and again in the dialog that acts on it. Two
    // copies drift, and the dialog is the one nobody would notice had gone stale.
    val handoverConsequence = t("app.settings.general.somebody_else_becomes_the_owner_and_2", "Somebody else becomes the owner and you stay on as an admin — you keep working, you just no longer have the last word.")

    ProjectSettingsSurface {
        // The form measure, inside the shared 1128px page measure. A rename field stretched to
        // the full page turns reading a label into a head movement, which is the reason
        // `SettingsSection` has a narrow default at all; this page has no section wrapper to
        // inherit it from, so it names the same measure.
        Column(Modifier.fillMaxWidth().gap(Space.XXL).maxWidth(SettingsFormMeasure)) {
            PageHeader(
                t("app.settings.general.workspace", "Workspace"),
                t("app.settings.general.what_this_workspace_is_called_and", "What this workspace is called, and who it belongs to."),
                mark = { LSettingsIcon() },
            )

            // Belonging to no workspace is not a session still loading. `WorkspaceSelection` falls
            // back to the strongest membership, so null here means this account is in no workspace at
            // all — and "your session has not loaded yet" told the one reader who has to act that
            // waiting would fix it. `/app` and `/app/start` have always answered this with the action
            // that does.
            if (workspace == null) {
                LEmptyState(
                    title = t("app.settings.general.you_are_not_in_a_workspace", "You are not in a workspace yet"),
                    description = t("app.settings.general.a_workspace_holds_your_projects_your", "A workspace holds your projects, your team and your billing. " +
                        "Create one, or ask whoever invited you to send the invitation again."),
                ) {
                    LButton(
                        t("app.settings.general.create_a_workspace", "Create a workspace"),
                        { ctx.router.tryRoutingTo("/app/workspaces") },
                    )
                }
                return@Column
            }

            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    Row(Modifier.gap(Space.SM)) {
                        LSectionTitle(workspace.name, Modifier.fontSize(TypeScale.HeadingSmall))
                        // The word, not the enum. `role.name.lowercase()` reached the reader as
                        // "translator" in every language and is one rename of a wire constant away
                        // from relabelling somebody's own access.
                        LBadge(workspace.role.label())
                    }
                    // Counted through `tPlural`, because "1 members" is English's own rule written as
                    // string concatenation, on the settings page of a product that sells real plurals.
                    LMuted(
                        tPlural(
                            "app.settings.general.count.member",
                            workspace.memberCount,
                            "{count} member",
                            "{count} members",
                        ) +
                            " · " +
                            tPlural(
                                "app.settings.general.count.project",
                                workspace.projectCount,
                                "{count} project",
                                "{count} projects",
                            ) +
                            " · " + workspace.plan.displayName,
                    )

                    if (canManage) {
                        LTextField(name, { name = it }, label = t("app.settings.general.workspace_name", "Workspace name"))
                        LTextField(slug, { slug = it }, label = t("app.settings.general.workspace_address", "Workspace address"))
                        // The honest note. People reasonably assume a slug change breaks links and
                        // therefore never rename — so the workspace keeps a name nobody uses.
                        LMuted(t("app.settings.general.lingvo_dev_app_the_workspace_address_2", "lingvo.dev/app — the workspace address appears only in settings URLs. Project links and lingvo.yaml use the project's own address, so renaming this breaks nothing anyone has written down."))
                        LButton(
                            t("app.settings.general.save", "Save"),
                            {
                                scope.launch {
                                    saving = true
                                    // `finally`, because the helper rethrows cancellation: a trailing
                                    // `saving = false` is skipped on that path and the button it gates
                                    // would stay disabled.
                                    try {
                                        settingsOperationResult {
                                            ApiClient.renameWorkspace(
                                                workspace.slug,
                                                name.trim(),
                                                slug.trim().takeIf { it != workspace.slug },
                                            )
                                        }.fold(
                                            {
                                                // The slug may have changed underneath the session;
                                                // reload it before anything else tries to address the
                                                // workspace by its old name.
                                                Session.loadMe()
                                                if (slug.trim() != workspace.slug) {
                                                    WorkspaceSelection.select(slug.trim())
                                                }
                                                toasts.show(
                                                    tr("app.settings.general.workspace_updated", "Workspace updated"),
                                                    LToastVariant.SUCCESS,
                                                    4_000,
                                                )
                                            },
                                            { failure ->
                                                toasts.show(
                                                    failure.message?.takeIf(String::isNotBlank)
                                                        ?: tr("app.settings.general.couldnt_update_the_workspace", "Couldn't update the workspace."),
                                                    LToastVariant.DANGER,
                                                    TOAST_UNTIL_DISMISSED,
                                                )
                                            },
                                        )
                                    } finally {
                                        saving = false
                                    }
                                }
                            },
                            enabled = name.isNotBlank() && !saving,
                            loading = saving,
                        )
                    } else {
                        // Read-only, and visibly so — not a disabled form, which invites clicking.
                        LMuted(t("app.settings.general.only_an_owner_or_admin_can", "Only an owner or admin can rename this workspace."))
                    }
                }
            }

            // **One region, announced once.** Three destructive cards stood in the same stack as the
            // rename form, distinguished only by the colour of their buttons — so the page read as
            // "settings, settings, settings, delete everything" with nothing between the third and the
            // fourth. Ordered least to most destructive: leave, hand over, delete. An owner who lands
            // on "delete" while looking for "leave" is the mistake this ordering exists to prevent.
            LSectionHeading(
                t("app.settings.general.danger_zone", "Danger zone"),
                t(
                    "app.settings.general.everything_below_here_removes_access_or",
                    "Everything below here removes access or removes the workspace. None of it is undone by pressing back.",
                ),
                icon = { LDangerIcon() },
            )
            LCard(Modifier.fillMaxWidth()) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LSectionTitle(t("app.settings.general.leave_this_workspace", "Leave this workspace"))
                    LMuted(t("app.settings.general.you_lose_access_to_its_projects_2", "You lose access to its projects. Your translations stay — they belong to the workspace, not to you."))
                    if (isOwner) {
                        LMuted(t("app.settings.general.as_the_owner_you_have_to", "As the owner you have to hand it over first, or delete it."))
                    }
                    LButton(
                        t("app.settings.general.leave_workspace", "Leave workspace"),
                        { confirmingLeave = true },
                        variant = LButtonVariant.DANGER,
                        enabled = !isOwner,
                    )
                }
            }

            if (isOwner) {
                LDivider(label = t("app.settings.general.handing_it_over", "Handing it over"))
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        LSectionTitle(t("app.settings.general.hand_this_workspace_over", "Hand this workspace over"))
                        LMuted(handoverConsequence)
                        // A failed member list used to render as "There is nobody else in this
                        // workspace yet", which tells an owner their colleagues are gone and hides
                        // the only control that moves the workspace. Empty and unreachable are not
                        // the same answer.
                        LAsyncContent(
                            members,
                            rows = 2,
                            loadingLabel = t("app.settings.general.loading_who_could_take_this_over", "Loading who could take this over"),
                            subject = t("app.settings.general.this_workspaces_members", "this workspace's members"),
                            onRetry = { dangerKey++ },
                        ) { loaded ->
                            val candidates = loaded?.members
                                ?.filter { it.userId != Session.user?.id }
                                .orEmpty()
                            if (candidates.isEmpty()) {
                                // The honest blocker: there is nobody to hand it to.
                                LMuted(t("app.settings.general.there_is_nobody_else_in_this", "There is nobody else in this workspace yet. Invite somebody first."))
                            } else {
                                // The select and the button share one wrapper, so the space between
                                // them is set here — the card's own gap falls outside it.
                                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                                    LSelect(
                                        value = transferTo,
                                        // A blank first option, because a select whose state is "" while
                                        // the browser paints the first name looks like a choice somebody
                                        // made — and the handover used to take that name.
                                        options = listOf(LSelectOption("", t("app.settings.general.choose_somebody", "Choose somebody"))) +
                                            candidates.map { LSelectOption(it.userId, "${it.name} — ${it.email}") },
                                        onValueChange = { transferTo = it },
                                        label = t("app.settings.general.new_owner", "New owner"),
                                    )
                                    LButton(
                                        t("app.settings.general.transfer_ownership", "Transfer ownership"),
                                        {
                                            transferError = null
                                            pendingTransfer = candidates.firstOrNull { it.userId == transferTo }
                                        },
                                        variant = LButtonVariant.DANGER,
                                        // No fallback to the first candidate: an untouched select must
                                        // not decide who owns the workspace. Matched against the list
                                        // rather than merely non-blank, because a selection that the
                                        // reloaded member list no longer contains resolves to null and
                                        // the click would open nothing — a button that looks live and
                                        // does nothing.
                                        enabled = candidates.any { it.userId == transferTo },
                                    )
                                }
                            }
                        }
                    }
                }

                LDivider(label = t("app.settings.general.deleting_it", "Deleting it"))
                LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        LSectionTitle(t("app.settings.general.delete_this_workspace", "Delete this workspace"))
                        // The failure is answered here rather than folded into "Loading…": the `else`
                        // that once stood in its place swallowed `Async.Failure`, and an owner whose
                        // deletion-scope request failed sat on that sentence for good, with no error,
                        // no retry and no way to reach the button.
                        LAsyncContent(
                            deletionScope,
                            rows = 1,
                            loadingLabel = t("app.settings.general.loading_what_this_would_remove", "Loading what this would remove…"),
                            subject = t("app.settings.general.this_workspace", "this workspace"),
                            onRetry = { dangerKey++ },
                        ) { counts ->
                            if (counts == null) {
                                // Not a failure: no workspace was selected, so the request never ran.
                                // Deliberately not the refusal a failure is given.
                                LMuted(t("app.settings.general.loading_what_this_would_remove", "Loading what this would remove…"))
                            } else if (counts.blockedBySubscription) {
                                // The server refuses this outright; the page says why rather than
                                // presenting a button that fails.
                                LAlert(
                                    t("app.settings.general.cancel_the_subscription_first", "Cancel the subscription first"),
                                    t("app.settings.general.deleting_a_workspace_does_not_stop", "Deleting a workspace does not stop a payment. Cancel the plan, then come back — otherwise you would keep being charged for something that no longer exists."),
                                    variant = LAlertVariant.DANGER,
                                )
                            } else {
                                // The sentence and the button share one wrapper, so the space between
                                // them is set here — the card's own gap falls outside it.
                                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                                    // Counted, not estimated. A number somebody reads before typing a
                                    // slug has to be the real one.
                                    LMuted(deletionImpact(counts))
                                    LButton(
                                        t("app.settings.general.delete_workspace", "Delete workspace"),
                                        { deleteError = null; confirmingDelete = true },
                                        variant = LButtonVariant.DANGER,
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    val handover = pendingTransfer
    // A name, not "somebody": an invited account can still be carrying a blank display name, and a
    // dialog headed "Make  the owner?" is the one place that must not be vague.
    val handoverName = handover?.name?.takeIf(String::isNotBlank) ?: handover?.email.orEmpty()
    LConfirmDialog(
        open = handover != null,
        // The recipient is named here because the password field was the only friction on this
        // handover and it never said who was about to be handed the workspace.
        title = tf("app.settings.general.make_person_the_owner", "Make {person} the owner?", "person" to handoverName),
        confirmLabel = t("app.settings.general.transfer_ownership", "Transfer ownership"),
        onConfirm = {
            val recipient = pendingTransfer ?: return@LConfirmDialog
            if (transferPassword.isBlank()) {
                // `LConfirmDialog` cannot gate its confirm button on a field in the content slot,
                // so the refusal is stated instead of hidden behind a button that does nothing.
                transferError = tr("app.settings.general.your_password_is_required", "Your password is required.")
                return@LConfirmDialog
            }
            transferring = true
            transferError = null
            scope.launch {
                // `finally`, because the helper rethrows cancellation: a trailing
                // `transferring = false` is skipped on that path and the dialog would stay locked.
                try {
                    settingsOperationResult {
                        ApiClient.transferWorkspace(workspaceSlug, recipient.userId, transferPassword)
                    }.fold(
                        {
                            transferPassword = ""
                            pendingTransfer = null
                            Session.loadMe()
                            dangerKey++
                            toasts.show(
                                tr("app.settings.general.handed_over_you_are_an_admin", "Handed over. You are an admin here now."),
                                LToastVariant.SUCCESS,
                            )
                        },
                        { failure ->
                            transferError = failure.message?.takeIf(String::isNotBlank)
                                ?: tr("app.settings.general.couldnt_transfer_ownership", "Couldn't transfer ownership.")
                        },
                    )
                } finally {
                    transferring = false
                }
            }
        },
        onCancel = {
            pendingTransfer = null
            transferPassword = ""
            transferError = null
        },
        consequenceTitle = t("app.settings.general.you_stop_being_the_owner", "You stop being the owner"),
        consequence = handoverConsequence,
        busy = transferring,
        error = transferError,
        content = {
            LTextField(
                value = transferPassword,
                onValueChange = { transferPassword = it },
                modifier = Modifier.fillMaxWidth(),
                label = t("app.settings.general.your_password", "Your password"),
                enabled = !transferring,
            )
            // Said plainly, because it is the reason the field is there at all.
            LMuted(t("app.settings.general.asked_for_because_a_session_left", "Asked for because a session left open on an unlocked laptop is the risk here."))
        },
    )

    val deletionCounts = (deletionScope as? Async.Success)?.value
    if (deletionCounts != null && !deletionCounts.blockedBySubscription) {
        LConfirmDialog(
            open = confirmingDelete,
            title = tf("app.settings.general.delete_workspace_2", "Delete {workspace}?", "workspace" to workspaceSlug),
            confirmLabel = t("app.settings.general.delete_workspace", "Delete workspace"),
            onConfirm = {
                if (workspaceSlug.isBlank()) return@LConfirmDialog
                deleting = true
                deleteError = null
                scope.launch {
                    // `finally`, because the helper rethrows cancellation: a trailing
                    // `deleting = false` is skipped on that path and the dialog would stay locked.
                    try {
                        settingsOperationResult {
                            ApiClient.deleteWorkspace(workspaceSlug, workspaceSlug, deletePassword)
                        }.fold(
                            {
                                Session.loadMe()
                                ctx.router.tryRoutingTo("/app/workspaces")
                            },
                            { failure ->
                                deleteError = failure.message?.takeIf(String::isNotBlank)
                                    ?: tr("app.settings.general.couldnt_delete_this_workspace", "Couldn't delete this workspace.")
                            },
                        )
                    } finally {
                        deleting = false
                    }
                }
            },
            onCancel = {
                confirmingDelete = false
                deletePassword = ""
                deleteError = null
            },
            consequenceTitle = t("app.settings.general.this_cannot_be_undone", "This cannot be undone"),
            consequence = deletionImpact(deletionCounts),
            typeToConfirm = workspaceSlug,
            typeToConfirmLabel = tf(
                "app.settings.general.type_workspace_to_confirm",
                "Type {workspace} to confirm",
                "workspace" to workspaceSlug,
            ),
            busy = deleting,
            error = deleteError,
            content = {
                LTextField(
                    value = deletePassword,
                    onValueChange = { deletePassword = it },
                    modifier = Modifier.fillMaxWidth(),
                    label = t("app.settings.general.your_password", "Your password"),
                    enabled = !deleting,
                )
            },
        )
    }

    // The same action as `Workspaces.kt` offers, and it used to be told differently: the confirm
    // fired without a busy state, closed the dialog before awaiting the request, and dropped the
    // server's refusal into a toast that outlived the screen it belonged to. Its sentence about
    // what you lose was shorter too. One dialog, one promise.
    LConfirmDialog(
        open = confirmingLeave,
        title = tf(
            "app.settings.general.leave_workspace_2",
            "Leave {workspace}?",
            "workspace" to workspace?.name.orEmpty(),
        ),
        confirmLabel = t("app.settings.general.leave", "Leave"),
        onConfirm = {
            val slugToLeave = workspace?.slug ?: return@LConfirmDialog
            scope.launch {
                leaving = true
                leaveError = null
                try {
                    settingsOperationResult { ApiClient.leaveWorkspace(slugToLeave) }.fold(
                        {
                            Session.loadMe()
                            confirmingLeave = false
                            ctx.router.tryRoutingTo("/app")
                        },
                        { failure ->
                            leaveError = failure.message?.takeIf(String::isNotBlank)
                                ?: tr("app.settings.general.couldnt_leave", "Couldn't leave.")
                        },
                    )
                } finally {
                    leaving = false
                }
            }
        },
        onCancel = {
            confirmingLeave = false
            leaveError = null
        },
        consequenceTitle = t("app.settings.general.this_takes_effect_immediately", "This takes effect immediately"),
        consequence = t("app.settings.general.you_will_need_a_new_invitation", "You lose access to every project in it, and only an owner or admin of " +
            "that workspace can invite you back."),
        busy = leaving,
        error = leaveError,
    )

    ToastHost(toasts)
}

/**
 * What deleting removes, in the number of forms the reader's language actually has.
 *
 * The counts are substituted into the sentence rather than concatenated around it: a translation
 * has to be free to put "every glossary term" where its own grammar wants it, and the sentence is
 * read twice — once on the card, once in the dialog that acts on it — so it lives in one place.
 */
@Composable
private fun deletionImpact(counts: WorkspaceDeletionScopeDto): String =
    tf(
        "app.settings.general.this_removes_projects_keys_translations_and",
        "This removes {projects}, {keys}, {translations} and every glossary term — permanently, for everyone.",
        "projects" to tPlural(
            "app.settings.general.count.project",
            counts.projects,
            "{count} project",
            "{count} projects",
        ),
        "keys" to tPlural("app.settings.general.count.key", counts.keys, "{count} key", "{count} keys"),
        "translations" to tPlural(
            "app.settings.general.count.translation",
            counts.translations,
            "{count} translation",
            "{count} translations",
        ),
    )
